import { Stack } from '@mui/material';
import { Tool } from '@procureai/agents';
import { CuriosityNamespaceType, useCuriosityTranslation } from '@procureai/curiosity-ui-mui';
import { useTranslation } from 'react-i18next';

import { BaseEntityChip } from '../BaseEntityChip';
import { ChatLoadingIndicator } from '../ChatMessage/indicators/ChatLoadingIndicator';
import { ChatToolResultIndicator } from '../ChatMessage/indicators/ChatToolResultIndicator';
import { SvbrgSearchSupplierInput, SvbrgSearchSupplierOutput } from '@/generated/gql/graphql';
import { I18nNamespace, ModuleEntityType } from '@/legacy/common/types';

import { ToolResultComponent } from '.';
import { isWildcardOrEmptyQuery } from './toolLabel.utils';

export const SupplierTool: ToolResultComponent<
  Tool.searchSupplier,
  SvbrgSearchSupplierInput,
  SvbrgSearchSupplierOutput
> = ({ tool, toolName }) => {
  const { t } = useTranslation(I18nNamespace.intake, { keyPrefix: `Chat.tools.${toolName}` });
  const { ct: ctSupplier } = useCuriosityTranslation(CuriosityNamespaceType.SUPPLIER);

  // Resolve a human-readable label for the loading state depending on how the agent called.
  //   - by ids → comma-joined resolved names (Curiosity lookup acceptable here — during
  //     streaming we don't yet have the tool output to fall back on)
  //   - by free-text name → the name
  //   - wildcard/empty → generic "searching suppliers..."
  const callMessage = (() => {
    const ids = tool.input?.supplierIds;
    if (ids && ids.length > 0) {
      const names = ids.map((id) => ctSupplier(id)?.value || 'Supplier').join(', ');
      return t('call', { supplierNames: names });
    }
    const name = tool.input?.name;
    if (isWildcardOrEmptyQuery(name)) {
      return t('callNoQuery');
    }
    return t('call', { supplierNames: name });
  })();

  switch (tool.state) {
    case 'input-available':
    case 'input-streaming':
      return <ChatLoadingIndicator message={callMessage} />;

    case 'output-available': {
      const suppliers = tool.output.suppliers ?? [];

      // Zero-result case: render a non-interactive indicator. Same pattern as BUG-2062 fix
      // for POs — the agent describes the absence in text; no value in an empty expandable.
      if (suppliers.length === 0) {
        return <ChatToolResultIndicator message={t('resultEmpty')} defaultOpen={false} disabled={true} />;
      }

      return (
        <ChatToolResultIndicator message={t('result', { count: suppliers.length })} defaultOpen={true} disabled={false}>
          <Stack direction="row" flexWrap="wrap" gap={1} sx={{ p: 2 }}>
            {suppliers.map((supplier) => (
              <BaseEntityChip
                key={supplier.id}
                entityType={ModuleEntityType.SUPPLIER}
                uuid={supplier.id}
                name={supplier.name}
              />
            ))}
          </Stack>
        </ChatToolResultIndicator>
      );
    }

    default:
      return null;
  }
};
